/**
 * F45 (Wave 2D) — analytics alert-rules page.
 *
 * Route: `/analytics/alert-rules`. Permission: `analytics:read` (view). Editing
 * a rule additionally needs `analytics:manage_alerts` (super_admin only — D4) —
 * the in-page check `adminUser.permissions.includes('analytics:manage_alerts')`
 * renders the rules read-only otherwise (the F44 two-layer-gate precedent).
 *
 * The 3 rules from GET /admin/analytics/alert-rules (email-only — A1):
 *   - dau_drop_alert         → thresholdPercent (% WoW)
 *   - funnel_step_drop_alert → thresholdPercent (pp WoW)
 *   - weekly_product_digest  → digestDow + digestHour
 * Each carries an enable Switch + Save → PATCH /admin/analytics/alert-rules/:ruleType.
 */
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, NumberInput, Skeleton, Switch, useToast } from '@bravemove/ui-web';
import type {
  AnalyticsAlertRule,
  AnalyticsAlertType,
  UpdateAnalyticsAlertRuleInput,
} from '@bravemove/types';
import { adminAnalyticsApi } from '../../api/analytics';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';

export function AnalyticsAlertRulesPage() {
  const { t } = useI18n();
  const { adminUser } = useAuth();
  const canEdit = adminUser?.permissions.includes('analytics:manage_alerts') ?? false;

  const [rules, setRules] = useState<readonly AnalyticsAlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRules(await adminAnalyticsApi.listAlertRules());
    } catch {
      setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchRules();
  }, [fetchRules]);

  const onSaved = (rule: AnalyticsAlertRule) => {
    setRules((prev) => prev.map((r) => (r.ruleType === rule.ruleType ? rule : r)));
  };

  return (
    <div className="space-y-6" data-testid="analytics-alert-rules-page">
      <PageHeader
        eyebrow="Analytics"
        title={t('admin:analytics.alertRules.title')}
        subtitle={t('admin:analytics.alertRules.subtitle')}
      />
      <AnalyticsNav />

      {!canEdit ? (
        <div data-testid="analytics-alert-rules-readonly-notice">
          <Banner tone="info">{t('admin:analytics.alertRules.readonlyNotice')}</Banner>
        </div>
      ) : null}

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="analytics-alert-rules-loading">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {rules.map((rule) => (
            <AlertRuleCard key={rule.ruleType} rule={rule} canEdit={canEdit} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
}

function AlertRuleCard({
  rule,
  canEdit,
  onSaved,
}: {
  rule: AnalyticsAlertRule;
  canEdit: boolean;
  onSaved: (rule: AnalyticsAlertRule) => void;
}) {
  const { t } = useI18n();
  const { push } = useToast();

  const [enabled, setEnabled] = useState(rule.enabled);
  const [thresholdPercent, setThresholdPercent] = useState(rule.thresholdPercent ?? 0);
  const [digestDow, setDigestDow] = useState(rule.digestDow ?? 1);
  const [digestHour, setDigestHour] = useState(rule.digestHour ?? 9);
  const [saving, setSaving] = useState(false);

  const isDigest = rule.ruleType === 'weekly_product_digest';
  const isFunnelDrop = rule.ruleType === 'funnel_step_drop_alert';
  const showThreshold = !isDigest;

  const onSave = async () => {
    setSaving(true);
    try {
      const input: UpdateAnalyticsAlertRuleInput = {
        enabled,
        ...(showThreshold ? { thresholdPercent } : {}),
        ...(isDigest ? { digestDow, digestHour } : {}),
      };
      const next = await adminAnalyticsApi.updateAlertRule(
        rule.ruleType as AnalyticsAlertType,
        input,
      );
      onSaved(next);
      push({ tone: 'success', title: t('admin:analytics.alertRules.saved') });
    } catch {
      push({ tone: 'danger', title: t('admin:analytics.alertRules.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-4"
      data-testid={`analytics-alert-rule-${rule.ruleType}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[14px] font-bold text-ink">
          {t(`admin:analytics.alertRules.${rule.ruleType}`)}
        </h2>
        <Switch
          label={t('admin:analytics.alertRules.enabled')}
          checked={enabled}
          disabled={!canEdit}
          onChange={(e) => setEnabled(e.target.checked)}
          data-testid={`analytics-alert-enabled-${rule.ruleType}`}
        />
      </div>

      {showThreshold ? (
        <div>
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
            {isFunnelDrop
              ? t('admin:analytics.alertRules.thresholdPercentPoints')
              : t('admin:analytics.alertRules.thresholdPercent')}
          </label>
          <NumberInput
            value={thresholdPercent}
            min={0}
            max={100}
            disabled={!canEdit}
            onChange={setThresholdPercent}
            data-testid={`analytics-alert-threshold-${rule.ruleType}`}
          />
        </div>
      ) : null}

      {isDigest ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
              {t('admin:analytics.alertRules.digestDow')}
            </label>
            <NumberInput
              value={digestDow}
              min={0}
              max={6}
              disabled={!canEdit}
              onChange={setDigestDow}
              data-testid={`analytics-alert-dow-${rule.ruleType}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">
              {t('admin:analytics.alertRules.digestHour')}
            </label>
            <NumberInput
              value={digestHour}
              min={0}
              max={23}
              disabled={!canEdit}
              onChange={setDigestHour}
              data-testid={`analytics-alert-hour-${rule.ruleType}`}
            />
          </div>
        </div>
      ) : null}

      <p
        className="text-[11px] text-ink-3"
        data-testid={`analytics-alert-lastfired-${rule.ruleType}`}
      >
        {t('admin:analytics.alertRules.lastFired')}:{' '}
        {rule.lastFiredAt ?? t('admin:analytics.alertRules.never')}
      </p>

      {canEdit ? (
        <div className="flex justify-end">
          <Button
            size="sm"
            loading={saving}
            onClick={onSave}
            data-testid={`analytics-alert-save-${rule.ruleType}`}
          >
            {saving ? t('admin:analytics.alertRules.saving') : t('admin:analytics.alertRules.save')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export default AnalyticsAlertRulesPage;
